import { APP_SEARCH_FAILED_MESSAGE } from '@app-state/app.const';
import { Injectable, inject } from '@angular/core';
import {
  Country,
  GetExcursionCalendarResponse,
  GetExcursionSearchOptionsResponse,
  SearchOnlyExcursionPackagesResponse
} from '@appTypes/api.types';
import { deepClean } from '@core/misc/misc.utils';
import { DataLoadStatusModel, RequestStateModel } from '@core/store/store.models';
import { RequestStateParams } from '@core/store/store.types';
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store';
import { insertItem, patch } from '@ngxs/store/operators';
import { CatalogueApiService } from '@shared/services/api/catalogue-api.service';
import { isEmpty } from 'lodash-es';
import { errorResponseWithTrace } from '@core/interceptors/api.interceptor';
import { EMPTY, catchError, switchMap, tap, throwError, timer } from 'rxjs';
import {
  ExcursionsEmptyDayInfo,
  excursionCalendarKey,
  excursionCalendarParamsOfSearch,
  isDayOnSale,
  nearestOnSaleDays
} from '../excursions-calendar.utils';
import { ExcursionsApiService } from '../services/excursions-api.service';
import {
  excursionsGetDestinationCountries,
  excursionsGetSearchOptions,
  excursionsLoadCalendar,
  excursionsPollSearchProgress,
  excursionsReset,
  excursionsResetSearchResult,
  excursionsSearchPackages,
  excursionsSetKindsFilter
} from './excursions.actions';
import { ExcursionsCalendarModel, ExcursionsPageDataModel, ExcursionsStateModel } from './excursions.model';

/** shown when a search finds nothing; the page adds the calendar's view of the day (nothing operates + nearest days) when it has it */
export const NO_EXCURSIONS_ON_SALE_MESSAGE = 'No excursions are on sale for this destination and day - try another day';

/** one calendar selector per parameter set, so the store (and a test double keyed by selector) sees the same selector each time */
const calendarSelectors = new Map<
  string,
  (state: ExcursionsStateModel) => RequestStateModel<GetExcursionCalendarResponse>
>();

const defaults = new ExcursionsStateModel();
/** R-PX2: poll pace when the answer names none, and failed polls in a row before the page keeps the core rows. */
const PROGRESS_POLL_DEFAULT_MS = 1000;
const PROGRESS_POLL_MAX_FAILURES = 3;

/** TravelDesk Plan 2b - the Excursions tab: one live search (D-21) per Search click, the kinds filter applied on the page. */
@State<ExcursionsStateModel>({ name: 'excursions', defaults })
@Injectable()
export class ExcursionsState {
  readonly #api = inject(ExcursionsApiService);
  readonly #catalogueApi = inject(CatalogueApiService);

  @Action(excursionsReset)
  excursionsReset({ setState }: StateContext<ExcursionsStateModel>) {
    return setState(defaults);
  }

  // website-web#158 (R-EUI10): no cancelUncompleted - a second click while loading returns on the guard below; cancelling would abort the running
  // search and leave the spinner forever
  @Action(excursionsSearchPackages)
  excursionsSearchPackages({ getState, patchState, dispatch }: StateContext<ExcursionsStateModel>) {
    const {
      searchResults: { loadState },
      searchForm
    } = getState();
    if (loadState.status === 'loading') return;
    if (!searchForm.model) return;
    const payload = deepClean(searchForm.model);
    if (isEmpty(payload)) return;

    patchState({
      searchResults: new RequestStateModel<SearchOnlyExcursionPackagesResponse>(new DataLoadStatusModel('loading')),
      kindsFilter: [],
      lastSearch: null
    });
    // R-PX1: the core rows at once, the live check polled (online-api ignores it where switched off)
    return this.#api.searchOnlyExcursionPackages({ ...payload, progressive: true }).pipe(
      tap((response) => {
        if (response.searchComplete === false && response.progressId)
          dispatch(
            new excursionsPollSearchProgress(response.progressId, response.pollAfterMs ?? PROGRESS_POLL_DEFAULT_MS)
          );
        const empty = !response.packages?.length;
        patchState({
          lastSearch: empty ? payload : null, // only a search that found nothing is explained by the calendar - never a failed one
          searchResults: empty
            ? new RequestStateModel<SearchOnlyExcursionPackagesResponse>(
                // run 24: the suppliers' rows stay beside the empty state - the summary shows which source answered nothing
                Object.assign(new DataLoadStatusModel('error', NO_EXCURSIONS_ON_SALE_MESSAGE), {
                  additionlData: response.diagnostics ? { diagnostics: response.diagnostics } : undefined
                })
              )
            : new RequestStateModel<SearchOnlyExcursionPackagesResponse>(response)
        });
        // 27.09.2026: an empty day is explained by the calendar of its month - nothing operates, and which days do (already cached when
        // the agent picked the day in the calendar; read now when the day was typed)
        if (empty) {
          const params = excursionCalendarParamsOfSearch(payload);
          if (params) dispatch(new excursionsLoadCalendar(params, excursionCalendarKey(params)));
        }
      }),
      catchError((e) => {
        patchState({
          searchResults: new RequestStateModel<SearchOnlyExcursionPackagesResponse>(
            new DataLoadStatusModel(
              'error',
              e?.displayErrorMessage ?? APP_SEARCH_FAILED_MESSAGE,
              errorResponseWithTrace(e)
            )
          )
        });
        return throwError(() => e);
      })
    );
  }

  /**
   * R-PX2 / R-PX3: the running search's latest answer replaces the list (live availability merged into the same rows, same order rules); stops
   * when complete, when another search replaced it, or after PROGRESS_POLL_MAX_FAILURES failed polls (the core rows stand, marked complete).
   */
  @Action(excursionsPollSearchProgress, { cancelUncompleted: true })
  excursionsPollSearchProgress(
    { getState, patchState, dispatch }: StateContext<ExcursionsStateModel>,
    { progressId, delayMs, failures }: excursionsPollSearchProgress
  ) {
    const isCurrent = () => {
      const response = getState().searchResults.response;
      return response?.progressId === progressId && response.searchComplete === false;
    };
    return timer(Math.max(0, delayMs)).pipe(
      switchMap(() => {
        const { searchForm } = getState();
        if (!isCurrent() || !searchForm.model) return EMPTY;
        return this.#api
          .searchOnlyExcursionPackages({ ...deepClean(searchForm.model), progressive: true, progressId })
          .pipe(
            tap((response) => {
              if (!isCurrent()) return;
              patchState({ searchResults: new RequestStateModel<SearchOnlyExcursionPackagesResponse>(response) });
              if (response.searchComplete === false)
                dispatch(
                  new excursionsPollSearchProgress(progressId, response.pollAfterMs ?? PROGRESS_POLL_DEFAULT_MS)
                );
            }),
            catchError(() => {
              if (!isCurrent()) return EMPTY;
              if (failures + 1 < PROGRESS_POLL_MAX_FAILURES) {
                dispatch(new excursionsPollSearchProgress(progressId, delayMs * 2, failures + 1));
                return EMPTY;
              }
              const current = getState().searchResults.response;
              if (current)
                patchState({
                  searchResults: new RequestStateModel<SearchOnlyExcursionPackagesResponse>({
                    ...current,
                    searchComplete: true,
                    pollAfterMs: null
                  })
                });
              return EMPTY;
            })
          );
      })
    );
  }

  @Action(excursionsResetSearchResult)
  excursionsResetSearchResult({ patchState }: StateContext<ExcursionsStateModel>) {
    patchState({ searchResults: new RequestStateModel<SearchOnlyExcursionPackagesResponse>() });
  }

  @Action(excursionsSetKindsFilter)
  excursionsSetKindsFilter({ patchState }: StateContext<ExcursionsStateModel>, { kinds }: excursionsSetKindsFilter) {
    patchState({ kindsFilter: kinds });
  }

  /** 27.09.2026: one month of the excursions calendar per parameter set, read once and kept (the tours calendar's flight-dates pattern) */
  @Action(excursionsLoadCalendar)
  excursionsLoadCalendar(
    { getState, setState }: StateContext<ExcursionsStateModel>,
    { params, identifier }: excursionsLoadCalendar
  ) {
    const {
      pageData: { calendars }
    } = getState();
    const existing = calendars.find((c) => c.identifier === identifier);
    if (existing && existing.data.loadState.status !== 'error') return; // a month that failed to load is read again (C17: no silent blank)

    const updateState = (data: RequestStateModel<GetExcursionCalendarResponse>) =>
      setState(
        patch<ExcursionsStateModel>({
          pageData: patch<ExcursionsPageDataModel>({
            calendars: insertItem<ExcursionsCalendarModel>({ data, identifier })
          })
        })
      );
    const replace = (data: RequestStateModel<GetExcursionCalendarResponse>) =>
      setState(
        patch<ExcursionsStateModel>({
          pageData: patch<ExcursionsPageDataModel>({
            calendars: getState().pageData.calendars.map((c) =>
              c.identifier === identifier ? { data, identifier } : c
            )
          })
        })
      );
    (existing ? replace : updateState)(
      new RequestStateModel<GetExcursionCalendarResponse>(new DataLoadStatusModel('loading'))
    );
    return this.#api.getCalendar(params).pipe(
      tap((response) => replace(new RequestStateModel<GetExcursionCalendarResponse>(response ?? { days: [] }))),
      catchError((e) => {
        replace(
          new RequestStateModel<GetExcursionCalendarResponse>(
            new DataLoadStatusModel(
              'error',
              e?.displayErrorMessage ?? 'The excursions calendar could not be loaded',
              errorResponseWithTrace(e)
            )
          )
        );
        return throwError(() => e);
      })
    );
  }

  /** The destinations sold as "Excursions only" in the Destination Module (P-18): the operator's switch per country, not the Hotels list. */
  @Action(excursionsGetDestinationCountries, { cancelUncompleted: true })
  excursionsGetDestinationCountries(
    { getState, setState }: StateContext<ExcursionsStateModel>,
    { refresh }: excursionsGetDestinationCountries
  ) {
    const {
      pageData: { destinationCountries }
    } = getState();
    if (destinationCountries.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<Country[]>) =>
      setState(
        patch<ExcursionsStateModel>({
          pageData: patch<ExcursionsPageDataModel>({ destinationCountries: new RequestStateModel(stateParams) })
        })
      );
    updateState(new DataLoadStatusModel('loading'));
    return this.#catalogueApi.getDestinationCountries({ onlyHotels: false, salesMode: 'excursions' }).pipe(
      tap((response) => {
        setState(
          patch<ExcursionsStateModel>({
            pageData: patch<ExcursionsPageDataModel>({
              destinationDefaultCountry: response?.defaultCountryCode ?? null
            })
          })
        );
        updateState(response?.countries ?? []);
      }),
      catchError((e) => {
        updateState(new DataLoadStatusModel('error', e?.displayErrorMessage, errorResponseWithTrace(e)));
        return throwError(() => e);
      })
    );
  }

  /** 25.09.2026: the form's city / category choices of a country - read once per country, from the prices on sale there */
  @Action(excursionsGetSearchOptions, { cancelUncompleted: true })
  excursionsGetSearchOptions(
    { getState, setState }: StateContext<ExcursionsStateModel>,
    { countryCode }: excursionsGetSearchOptions
  ) {
    const { pageData } = getState();
    if (!countryCode) return;
    if (pageData.searchOptionsCountry === countryCode && pageData.searchOptions.loadState.status === 'completed')
      return;
    const updateState = (stateParams: RequestStateParams<GetExcursionSearchOptionsResponse>) =>
      setState(
        patch<ExcursionsStateModel>({
          pageData: patch<ExcursionsPageDataModel>({
            searchOptions: new RequestStateModel(stateParams),
            searchOptionsCountry: countryCode
          })
        })
      );
    updateState(new DataLoadStatusModel('loading'));
    return this.#api.getSearchOptions({ countryCode }).pipe(
      tap((response) => updateState(response ?? { cities: [], types: [] })),
      catchError((e) => {
        updateState(new DataLoadStatusModel('error', e?.displayErrorMessage));
        return throwError(() => e);
      })
    );
  }

  /* Selectors */

  @Selector([ExcursionsState])
  static destinationCountries({ pageData: { destinationCountries } }: ExcursionsStateModel) {
    return destinationCountries;
  }

  @Selector([ExcursionsState])
  static searchOptions({ pageData: { searchOptions } }: ExcursionsStateModel) {
    return searchOptions;
  }

  @Selector([ExcursionsState])
  static destinationDefaultCountry({ pageData: { destinationDefaultCountry } }: ExcursionsStateModel) {
    return destinationDefaultCountry;
  }

  /** The results with the kinds filter applied - a kind is read from the package's availability. */
  @Selector([ExcursionsState])
  static searchResults({ searchResults, kindsFilter }: ExcursionsStateModel) {
    if (kindsFilter?.length && searchResults.response?.packages) {
      const packages = searchResults.response.packages.filter((p) =>
        kindsFilter.includes(p.offer?.availability?.kind ?? '')
      );
      return { ...searchResults, response: { ...searchResults.response, packages } };
    }
    return searchResults;
  }

  @Selector([ExcursionsState])
  static kindsFilter({ kindsFilter }: ExcursionsStateModel) {
    return kindsFilter;
  }

  @Selector([ExcursionsState])
  static searchFormData({ searchForm: { model } }: ExcursionsStateModel) {
    return model;
  }

  @Selector([ExcursionsState])
  static searchLoadStatus({ searchResults: { loadState } }: ExcursionsStateModel): DataLoadStatusModel {
    return loadState;
  }

  /** 27.09.2026: the calendar month of a parameter set - "loading" until it is in (the picker shows its bar meanwhile) */
  static calendarByParams(identifier: string) {
    let selector = calendarSelectors.get(identifier);
    if (!selector) {
      selector = createSelector([ExcursionsState], ({ pageData: { calendars } }: ExcursionsStateModel) => {
        const calendar = calendars.find((c) => c.identifier === identifier)?.data;
        if (calendar) return calendar;
        else return new RequestStateModel<GetExcursionCalendarResponse>(new DataLoadStatusModel('loading'));
      });
      calendarSelectors.set(identifier, selector);
    }
    return selector;
  }

  /** 27.09.2026: what the calendar says about the last searched day - null until the month is in (or when the day is not a date) */
  @Selector([ExcursionsState])
  static emptyDayInfo({ lastSearch, pageData: { calendars } }: ExcursionsStateModel): ExcursionsEmptyDayInfo | null {
    if (!lastSearch?.date) return null;
    const params = excursionCalendarParamsOfSearch(lastSearch);
    if (!params) return null;
    const calendar = calendars.find((c) => c.identifier === excursionCalendarKey(params))?.data;
    if (calendar?.loadState.status !== 'completed') return null;
    const days = calendar.response?.days ?? [];
    const dayOnSale = isDayOnSale(days, lastSearch.date);
    if (dayOnSale === null) return null;
    return { date: lastSearch.date, dayOnSale, nearestDays: nearestOnSaleDays(days, lastSearch.date) };
  }
}
